import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Credentials, CredentialsService } from './credentials.service';

import { Logger } from '../../services/logger/logger.service';
import moment from 'moment';

export interface LoginSuccess {
    type: 'success';
    credentials: Credentials;
}

export interface MfaRequired {
    type: 'mfa_required';
    preAuthToken: string;
}

export interface MfaSetupRequired {
    type: 'mfa_setup_required';
    preAuthToken: string;
}

export type LoginResponse = LoginSuccess | MfaRequired | MfaSetupRequired;

export interface MfaDevice {
    id: number;
    name: string;
    createdAt: moment.Moment;
    lastUsedAt: moment.Moment | null;
}

export type MfaReauthFactor = { code: string } | { password: string };

export interface MfaSetupResponse {
    deviceId: number;
    otpauthUrl: string;
    secret: string;
}

const LOGGER = new Logger('MfaService');

@Injectable({
    providedIn: 'root'
})
export class MfaService {
    private _credentialsService = inject(CredentialsService);
    private _httpClient = inject(HttpClient);

    /**
     * Confirme l'enrôlement d'un nouvel appareil en validant le premier code TOTP.
     * Retourne les JWT finaux en cas de succès.
     */
    confirm$(deviceId: number, code: string, preAuthToken?: string): Observable<Credentials> {
        const options: any = { withCredentials: true };
        if (preAuthToken) {
            options.headers = { Authorization: `PreAuth ${preAuthToken}` };
        }
        return this._httpClient
            .post(`/mfa/confirm/`, { device_id: deviceId, code }, options)
            .pipe(
                map((body: any) => {
                    const tokenData = { access: body.access };
                    this._credentialsService.setCredentials(tokenData);
                    return tokenData;
                })
            );
    }

    /**
     * Supprime un appareil TOTP confirmé.
     * Exige le pre-auth token `manage` délivré par `reauth$`.
     */
    deleteDevice$(id: number, preAuthToken: string): Observable<void> {
        return this._httpClient.delete<void>(`/mfa/devices/${id}/`, {
            headers: { Authorization: `PreAuth ${preAuthToken}` }
        });
    }

    /**
     * Retourne la liste des appareils TOTP confirmés de l'utilisateur.
     */
    getDevices$(): Observable<MfaDevice[]> {
        return this._httpClient
            .get<any[]>(`/mfa/devices/`)
            .pipe(
                map(devices => devices.map(d => ({
                    id: d.id,
                    name: d.name,
                    createdAt: moment(d.createdAt),
                    lastUsedAt: d.lastUsedAt ? moment(d.lastUsedAt) : null
                })))
            );
    }

    /**
     * Ré-authentifie l'utilisateur connecté avant d'ajouter ou de supprimer un appareil :
     * le code TOTP courant s'il a au moins un appareil, son mot de passe sinon.
     * Retourne le pre-auth token `manage` (10 minutes), à garder en mémoire seulement.
     */
    reauth$(factor: MfaReauthFactor): Observable<string> {
        return this._httpClient
            .post(`/mfa/reauth/`, factor)
            .pipe(map((body: any) => body.preAuthToken as string));
    }

    /**
     * Initialise l'enrôlement d'un nouvel appareil TOTP.
     * Retourne l'URI otpauth://, le secret base32 et l'id du device.
     */
    setup$(deviceName: string, preAuthToken?: string): Observable<MfaSetupResponse> {
        const options: any = {};
        if (preAuthToken) {
            options.headers = { Authorization: `PreAuth ${preAuthToken}` };
        }
        return this._httpClient
            .post(`/mfa/setup/`, { name: deviceName }, options)
            .pipe(
                map((body: any) => ({
                    deviceId: body.deviceId,
                    otpauthUrl: body.otpauthUrl,
                    secret: body.secret,
                }))
            );
    }

    /**
     * Valide un code TOTP pour un appareil déjà enrôlé.
     * Retourne les JWT finaux en cas de succès.
     */
    verify$(code: string, preAuthToken: string): Observable<Credentials> {
        return this._httpClient
            .post(`/token/mfa/`, { code }, {
                withCredentials: true,
                headers: { Authorization: `PreAuth ${preAuthToken}` }
            })
            .pipe(
                map((body: any) => {
                    const tokenData = { access: body.access };
                    this._credentialsService.setCredentials(tokenData);
                    return tokenData;
                })
            );
    }
}
